import {
  KoButton,
  KoDrawer,
  KoLoader,
  KoSelect,
  KoTable,
  KoText,
  KoTitle,
  KoTag,
  KoEmpty,
  KoPagination
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { ApiPlatformAPI } from '../services/apiplatform.service';
import { COLUMNS_AUDIT_LOGS, AUDIT_ACTION_CONFIG } from '../constants';
import ApiSubNav from './ApiSubNav';
import styles from './apiplatform.module.scss';

const ENTITY_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Service', value: 'service' },
  { label: 'Endpoint', value: 'endpoint' },
  { label: 'Collection', value: 'collection' },
  { label: 'Environment', value: 'environment' },
  { label: 'Test Suite', value: 'test_suite' }
];

const ACTION_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Created', value: 'created' },
  { label: 'Updated', value: 'updated' },
  { label: 'Deleted', value: 'deleted' },
  { label: 'Deployed', value: 'deployed' },
  { label: 'Deprecated', value: 'deprecated' },
  { label: 'Version Changed', value: 'version_changed' }
];

const MOCK_AUDIT_LOGS = [
  { _id: 'al-001', timestamp: '2026-02-24T10:30:00Z', action: 'updated', entityType: 'endpoint', entityName: '/v3/users/:id', user: 'John Doe', details: 'Added response schema validation', version: 'v3.2.1' },
  { _id: 'al-002', timestamp: '2026-02-24T09:15:00Z', action: 'created', entityType: 'endpoint', entityName: '/v4/payments/subscription/pause', user: 'Alice Park', details: 'New endpoint for subscription management', version: 'v4.1.0' },
  { _id: 'al-003', timestamp: '2026-02-24T08:00:00Z', action: 'deployed', entityType: 'service', entityName: 'Payment Gateway', user: 'CI/CD Pipeline', details: 'Deployed to production via release v4.1.0', version: 'v4.1.0' },
  { _id: 'al-004', timestamp: '2026-02-23T18:30:00Z', action: 'deprecated', entityType: 'endpoint', entityName: '/v1/billing/invoices', user: 'Bob Chen', details: 'Deprecated in favor of Payment Gateway endpoints', version: 'v1.0.8' },
  { _id: 'al-005', timestamp: '2026-02-23T16:00:00Z', action: 'deleted', entityType: 'collection', entityName: 'Legacy Billing Tests', user: 'Bob Chen', details: 'Removed obsolete test collection', version: '' },
  { _id: 'al-006', timestamp: '2026-02-23T14:20:00Z', action: 'version_changed', entityType: 'service', entityName: 'User Service', user: 'John Doe', details: 'Version bumped from v3.1.0 to v3.2.1', version: 'v3.2.1' },
  { _id: 'al-007', timestamp: '2026-02-23T11:00:00Z', action: 'updated', entityType: 'environment', entityName: 'Production', user: 'DevOps Bot', details: 'Updated API_KEY and JWT_SECRET variables', version: '' },
  { _id: 'al-008', timestamp: '2026-02-22T09:45:00Z', action: 'created', entityType: 'test_suite', entityName: 'Payment Refund Suite', user: 'Alice Park', details: 'New test suite for refund flow validation', version: '' },
  { _id: 'al-009', timestamp: '2026-02-22T08:30:00Z', action: 'updated', entityType: 'service', entityName: 'Search Engine', user: 'Data Team', details: 'Updated base URL and auth configuration', version: 'v2.3.1' },
  { _id: 'al-010', timestamp: '2026-02-21T16:00:00Z', action: 'created', entityType: 'environment', entityName: 'QA Sandbox', user: 'QA Team', details: 'New isolated QA testing environment', version: '' }
];

function AuditLogs() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ size: 50, page: 1 });

  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/apiplatform`
    : '/apiplatform';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'API Platform', path: basePath },
      { title: 'Audit Logs' }
    ]));
  }, [workspaceId]);

  useEffect(() => {
    fetchLogs();
  }, [workspaceId, pagination, entityFilter, actionFilter]);

  const fetchLogs = () => {
    setLoading(true);
    const params = { page: pagination.page, limit: pagination.size };
    if (entityFilter) params.entityType = entityFilter;
    if (actionFilter) params.action = actionFilter;

    ApiPlatformAPI.getAuditLogs(params).then(
      (res) => {
        const data = res?.data || res || [];
        setLogs(data.length > 0 ? data : MOCK_AUDIT_LOGS);
        setTotalCount(res?.totalCount || (data.length > 0 ? data.length : MOCK_AUDIT_LOGS.length));
        setLoading(false);
      },
      () => {
        setLogs(MOCK_AUDIT_LOGS);
        setTotalCount(MOCK_AUDIT_LOGS.length);
        setLoading(false);
      }
    );
  };

  const openDetail = (record) => {
    setSelectedLog(record);
    setDrawerOpen(true);
  };

  const columnsWithActions = [
    ...COLUMNS_AUDIT_LOGS,
    {
      label: '', id: 'actions', key: 'actions', width: 60,
      render: (_, record) => (
        <KoButton
          iconBtn
          icon={<KoIcon name='EyeOutlined' width={14} height={14} />}
          onClick={(e) => { e.stopPropagation(); openDetail(record); }}
        />
      )
    }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <ApiSubNav activeKey='audit-logs' />

      <KoToolBar
        title='Audit Logs'
        search={{ visible: false }}
        filter={{ visible: false, options: [] }}
        buttonList={[{
          label: 'Refresh',
          icon: <KoIcon color='#565555' name='ReloadOutlined' width={16} height={16} />,
          onClick: fetchLogs
        }]}
      />

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <KoSelect
          className={styles.filterItem}
          value={entityFilter}
          onChange={setEntityFilter}
          placeholder='Entity Type'
          options={ENTITY_TYPE_OPTIONS}
          allowClear
        />
        <KoSelect
          className={styles.filterItem}
          value={actionFilter}
          onChange={setActionFilter}
          placeholder='Action'
          options={ACTION_TYPE_OPTIONS}
          allowClear
        />
        <KoButton
          label='Clear Filters'
          icon={<KoIcon name='ClearOutlined' width={14} height={14} />}
          onClick={() => { setEntityFilter(''); setActionFilter(''); }}
        />
      </div>

      {logs.length === 0 && !isLoading && <KoEmpty description='No audit logs found' />}
      {logs.length > 0 && (
        <>
          <KoTable maxHeight={480} dataSource={logs} columns={columnsWithActions} rowKey={(r) => r._id || r.auditId} />
          <KoPagination totalCount={totalCount} pageSize={pagination.size} currentPage={pagination.page} onChange={(page, size) => setPagination({ page, size })} />
        </>
      )}

      {/* Audit Log Detail Drawer */}
      <KoDrawer
        title='Audit Log Detail'
        open={isDrawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedLog(null); }}
        width={'min(600px, 95vw)'}
      >
        {selectedLog && (
          <div>
            <div className={styles.drawerHeader}>
              <KoTag color={AUDIT_ACTION_CONFIG[selectedLog.action]?.color}>
                {AUDIT_ACTION_CONFIG[selectedLog.action]?.label || selectedLog.action}
              </KoTag>
              <KoTitle level={5} text={selectedLog.entityName || '—'} />
            </div>

            <div className={styles.detailMetaGrid}>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Timestamp</span>
                <span className={styles.detailValue}>{selectedLog.timestamp || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Entity Type</span>
                <span className={styles.detailValue}>{selectedLog.entityType || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>User</span>
                <span className={styles.detailValue}>{selectedLog.user || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>IP Address</span>
                <span className={styles.detailValue}>{selectedLog.ipAddress || '—'}</span>
              </div>
              <div className={styles.detailMetaItem}>
                <span className={styles.detailLabel}>Version</span>
                <span className={styles.detailValue}>{selectedLog.version || '—'}</span>
              </div>
            </div>

            {selectedLog.details && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Details</div>
                <KoText text={selectedLog.details} />
              </div>
            )}

            {selectedLog.before && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>Before</div>
                <div className={styles.auditDiffBlock}>
                  {typeof selectedLog.before === 'string' ? selectedLog.before : JSON.stringify(selectedLog.before, null, 2)}
                </div>
              </div>
            )}

            {selectedLog.after && (
              <div className={styles.detailSection}>
                <div className={styles.detailLabel}>After</div>
                <div className={styles.auditDiffBlock}>
                  {typeof selectedLog.after === 'string' ? selectedLog.after : JSON.stringify(selectedLog.after, null, 2)}
                </div>
              </div>
            )}
          </div>
        )}
      </KoDrawer>
    </div>
  );
}

export default AuditLogs;
